import PropTypes from 'prop-types';
import { createContext, useContext, useState } from 'react';

const CustomReportContext = createContext();

export const useCustomReportContext = () => {
  const context = useContext(CustomReportContext);
  if (!context) {
    throw new Error(
      'useCustomReportContext must be used within CustomReportProvider'
    );
  }
  return context;
};

const CustomReportProvider = ({ children }) => {
  // Wizard state
  const [currentStep, setCurrentStep] = useState(1);
  const [reportData, setReportData] = useState({
    // Step 1: Basic Definitions
    name: '',
    description: '',
    inactive: false,

    // Step 2: Data and Filters
    columns: [],
    filters: [],
    includeTicketNotes: false,
    includeTicketAppointments: false,

    // Step 3: Scheduling
    schedule: {
      enabled: false,
      frequency: 'DAILY',
      scheduleDetails: '', // For weekly: selected days
      dayOfMonth: '', // For monthly: selected day
      daysOfWeek: '', // For weekly: selected days
      executionTime: '08:00',
      executionInterval: 1,
      recipients: '',
      emailSubjectTemplate: '',
      emailBodyTemplate: '',
      sendAsExcel: false,
      sendAsPdf: false,
      sendAsCsv: false,
      inactive: false
    }
  });

  const updateReportData = data => {
    setReportData(prev => ({
      ...prev,
      ...data
    }));
  };

  const updateSchedule = scheduleData => {
    setReportData(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        ...scheduleData
      }
    }));
  };

  const updateColumns = columns => {
    setReportData(prev => ({
      ...prev,
      columns
    }));
  };

  const updateFilters = filters => {
    setReportData(prev => ({
      ...prev,
      filters
    }));
  };

  const resetReport = () => {
    setCurrentStep(1);
    setReportData({
      name: '',
      description: '',
      inactive: false,
      columns: [],
      filters: [],
      includeTicketNotes: false,
      includeTicketAppointments: false,
      schedule: {
        enabled: false,
        frequency: 'DAILY',
        scheduleDetails: '',
        daysOfWeek: '',
        dayOfMonth: '',
        executionTime: '08:00',
        executionInterval: 1,
        recipients: '',
        emailSubjectTemplate: '',
        emailBodyTemplate: '',
        sendAsExcel: false,
        sendAsPdf: false,
        sendAsCsv: true,
        inactive: false
      }
    });
  };

  const value = {
    currentStep,
    setCurrentStep,
    reportData,
    setReportData,
    updateReportData,
    updateSchedule,
    updateColumns,
    updateFilters,
    resetReport
  };

  return (
    <CustomReportContext.Provider value={value}>
      {children}
    </CustomReportContext.Provider>
  );
};

CustomReportProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default CustomReportProvider;
